import { useState } from 'react'
import { Mountain } from 'lucide-react'

import { TerrainLoadDialog } from '@/components/terrain/terrain-load-dialog'
import { Button } from '@/components/ui/button'
import type { TerrainOverlayResult } from '@/hooks/use-terrain-overlay'
import { useActivityStore } from '@/store/activity-store'

export interface TerrainControlsProps {
  activityId: string
  hasGps: boolean
  terrain: TerrainOverlayResult
}

/**
 * Timeline-header controls for the terrain overlay: "Load terrain
 * elevation" (behind the `TerrainLoadDialog` disclosure), then progress +
 * Cancel while loading, then Hide/Show + Remove once loaded. "Load
 * missing terrain tiles" appears only when an edit has moved the route
 * onto tiles that aren't cached -- the overlay never fetches on its own.
 */
export function TerrainControls({ activityId, hasGps, terrain }: TerrainControlsProps) {
  const loadTerrainForSelected = useActivityStore((s) => s.loadTerrainForSelected)
  const cancelTerrainLoad = useActivityStore((s) => s.cancelTerrainLoad)
  const setTerrainVisible = useActivityStore((s) => s.setTerrainVisible)
  const removeTerrain = useActivityStore((s) => s.removeTerrain)
  const [dialogOpen, setDialogOpen] = useState(false)

  const { status, progress, source, visible, error } = terrain.terrain
  const hasOverlay = source != null && status !== 'error'
  const noGpsHintId = `terrain-no-gps-${activityId}`

  return (
    <div className="flex flex-wrap items-center gap-2">
      {status === 'loading' && (
        <>
          <span role="status" className="text-muted-foreground">
            Loading terrain tiles
            {progress != null && progress.total > 0 ? ` ${progress.done}/${progress.total}` : ''}…
          </span>
          <Button variant="outline" size="sm" onClick={() => cancelTerrainLoad(activityId)}>
            Cancel
          </Button>
        </>
      )}

      {status !== 'loading' && !hasOverlay && (
        <>
          {status === 'error' && error != null && (
            <span role="alert" className="text-destructive max-w-xs truncate" title={error}>
              Terrain failed: {error}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={!hasGps}
            aria-describedby={hasGps ? undefined : noGpsHintId}
            className="max-sm:w-8 max-sm:px-0"
            title={status === 'error' ? 'Retry terrain elevation' : 'Load terrain elevation'}
            onClick={() => setDialogOpen(true)}
          >
            <Mountain className="h-3.5 w-3.5" aria-hidden="true" />
            {/* Icon-only on phones; the label stays the accessible name. */}
            <span className="max-sm:sr-only">
              {status === 'error' ? 'Retry terrain elevation' : 'Load terrain elevation'}
            </span>
          </Button>
          {!hasGps && (
            <span id={noGpsHintId} className="text-muted-foreground">
              Needs GPS positions
            </span>
          )}
        </>
      )}

      {hasOverlay && status !== 'loading' && (
        <>
          {terrain.overlay != null && terrain.overlay.unresolvedTiles > 0 && (
            <Button variant="outline" size="sm" onClick={() => void loadTerrainForSelected()}>
              Load missing terrain tiles ({terrain.overlay.unresolvedTiles})
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTerrainVisible(activityId, !visible)}
          >
            <Mountain className="h-3.5 w-3.5" aria-hidden="true" />
            {visible ? 'Hide terrain' : 'Show terrain'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => removeTerrain(activityId)}>
            Remove terrain
          </Button>
        </>
      )}

      <TerrainLoadDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  )
}
